// Home's set-up steps need to know two things the account's ledger can't say:
// whether the consumer has opened Credits and whether they've copied a request.
// Both are remembered in this browser only, per account; losing them only
// reopens a step.

import { useEffect, useState } from "preact/hooks";

export type Seen = "credits" | "copied";

const EVENT = "console-seen";
const key = (account: string, what: Seen) => `console-seen:${account}:${what}`;

const read = (account: string | null | undefined, what: Seen): boolean => {
  if (!account) return false;
  try { return localStorage.getItem(key(account, what)) === "1"; } catch { return false; }
};

export function markSeen(account: string | null | undefined, what: Seen) {
  if (!account || read(account, what)) return;
  try { localStorage.setItem(key(account, what), "1"); } catch { /* storage blocked: the step stays open */ }
  dispatchEvent(new Event(EVENT));
}

export function useSeen(account: string | null | undefined, what: Seen): boolean {
  const [seen, setSeen] = useState(() => read(account, what));
  useEffect(() => {
    const update = () => setSeen(read(account, what));
    update();
    addEventListener(EVENT, update);
    return () => removeEventListener(EVENT, update);
  }, [account, what]);
  return seen;
}

/** Whether a page's docked panel is open, remembered per page in this browser. */
export function usePanelOpen(page: string, initial: boolean): [boolean, (open: boolean) => void] {
  const k = `console-panel:${page}`;
  const [open, setOpen] = useState(() => {
    try { const v = localStorage.getItem(k); return v === null ? initial : v === "1"; } catch { return initial; }
  });
  return [open, (v) => { setOpen(v); try { localStorage.setItem(k, v ? "1" : "0"); } catch { /* storage blocked: lasts this visit */ } }];
}
